
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface PaymentMethod {
  id: string;
  stripe_payment_method_id: string;
  card_brand: string;
  card_last4: string;
  card_exp_month: number;
  card_exp_year: number;
  is_default: boolean;
  created_at: string;
}

export default function PaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('saved_payment_methods')
        .select('*')
        .eq('user_id', user.id)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPaymentMethods(data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const setDefaultPaymentMethod = async (paymentMethodId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Remove default from all cards
      await supabase
        .from('saved_payment_methods')
        .update({ is_default: false })
        .eq('user_id', user.id);

      // Set new default
      await supabase
        .from('saved_payment_methods')
        .update({ is_default: true })
        .eq('id', paymentMethodId);

      fetchPaymentMethods();
    } catch (error) {
      console.error('Error setting default payment method:', error);
    }
  };

  const deletePaymentMethod = async (paymentMethodId: string) => {
    if (!confirm('Are you sure you want to remove this payment method?')) return;

    try {
      await supabase
        .from('saved_payment_methods')
        .delete()
        .eq('id', paymentMethodId);

      fetchPaymentMethods();
    } catch (error) {
      console.error('Error deleting payment method:', error);
    }
  };

  const getCardIcon = (brand: string) => {
    switch (brand?.toLowerCase()) {
      case 'visa': return 'ri-visa-line';
      case 'mastercard': return 'ri-mastercard-line';
      case 'amex': return 'ri-bank-card-line';
      default: return 'ri-bank-card-line';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Payment Methods</h2>
            <p className="text-gray-600 text-sm">Manage your saved payment cards</p>
          </div>
          <button
            onClick={() => setShowAddCard(true)}
            className="px-4 py-2 bg-gradient-to-r from-[#FF6A00] to-[#FF8533] text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer font-semibold"
          >
            <i className="ri-add-line mr-2"></i>
            Add Card
          </button>
        </div>

        {paymentMethods.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-bank-card-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Payment Methods</h3>
            <p className="text-gray-600 mb-6">Add a payment method for faster checkout</p>
          </div>
        ) : (
          <div className="space-y-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className={`border-2 rounded-xl p-5 transition-all ${
                  method.is_default
                    ? 'border-[#FF6A00] bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0B3C5D] to-[#0B3C5D] rounded-xl flex items-center justify-center">
                      <i className={`${getCardIcon(method.card_brand)} text-white text-2xl`}></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-gray-900 capitalize">
                          {method.card_brand} •••• {method.card_last4}
                        </p>
                        {method.is_default && (
                          <span className="px-2 py-1 bg-[#FF6A00] text-white text-xs rounded-full font-semibold">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        Expires {method.card_exp_month.toString().padStart(2, '0')}/{method.card_exp_year}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {!method.is_default && (
                      <button
                        onClick={() => setDefaultPaymentMethod(method.id)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer font-semibold"
                      >
                        Set as Default
                      </button>
                    )}
                    <button
                      onClick={() => deletePaymentMethod(method.id)}
                      className="w-10 h-10 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center cursor-pointer"
                      title="Remove card"
                    >
                      <i className="ri-delete-bin-line text-xl"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <i className="ri-shield-check-fill text-blue-600 text-2xl"></i>
          <div>
            <h4 className="font-bold text-gray-900 mb-1">Secure Payment Processing</h4>
            <p className="text-sm text-gray-700">
              All payment information is encrypted and securely stored by Stripe. 
              We never store your full card details on our servers.
            </p>
          </div>
        </div>
      </div>

      {showAddCard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Add Payment Method</h3>
              <button
                onClick={() => setShowAddCard(false)}
                className="w-10 h-10 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-yellow-600 text-xl"></i>
                <p className="text-sm text-gray-700">
                  Payment methods are automatically saved when you complete a booking. 
                  You can also add cards during checkout.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowAddCard(false)}
              className="w-full py-3 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-colors whitespace-nowrap cursor-pointer font-semibold"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
